/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useEffect, useState} from 'react'
import {toAbsoluteUrl} from '../../../_metronic/helpers'
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore'
import db from '../../../services/firebase'
import {useAuth} from '../../modules/auth'
import {VendorListToolbar} from './components/header/VendorListToolbar'
import {Helmet} from 'react-helmet'
import {UpdateVendor} from './components/UpdateVendor'
import {PolicyListLoading} from './components/loading/PolicyListLoading'
import {Link} from 'react-router-dom'
import {showErrorToast, showSuccessToast} from 'app/components/ShowToast'
import ConfirmationModal from 'app/components/ConfirmationModal'
import Searchbar from 'app/components/Searchbar'
import OwnerDisplay from 'app/components/OwnerDisplay'
import Button from 'app/components/Button'
import useAuditContext from 'app/Hooks/useAuditContext'

type Props = {
  className: string
}

const VendorRisk: React.FC<Props> = ({className}) => {
  const {currentUser} = useAuth()
  const [isDropdownVisible, setDropdownVisible] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [filteredItems, setFilteredItems] = useState<any[]>([])
  const [showEmptyState, setShowEmptyState] = useState<boolean>(false)
  const [vendor, setVendor] = useState<any[]>([])
  const [selectedCategory, setSelectedCategory] = useState('')
  const [searchLength, setSearchLength] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [vendorID, setVendorID] = useState('')
  const [checkedItems, setCheckedItems] = useState<any[]>([])
  const [selectAll, setSelectAll] = useState<boolean>(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [selectedVendor, setSelectedVendor] = useState<any>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isMultiDeleteModalOpen, setIsMultiDeleteModalOpen] = useState(false)
  const [isMultiDeleting, setIsMultiDeleting] = useState(false)
  const {currentAuditContext} = useAuditContext()

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setShowEmptyState(true)
    }, 1000)
    return () => clearTimeout(timeoutId)
  }, [])

  useEffect(() => {
    setFilteredItems(vendor)
  }, [vendor])

  useEffect(() => {
    getVendor()
  }, [])

  const getVendor = async () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const PolicyRef = collection(tenantRef, 'vendors')

      const unsubscribe = onSnapshot(PolicyRef, (snapshot) => {
        const risksData = snapshot.docs
          .map((doc) => ({
            ...doc.data(),
            id: doc.id,
            archived: doc.data().archived,
          }))
          .filter((vendor) => !vendor.archived)
        setVendor(risksData)
      })

      return unsubscribe
    } catch (error) {
      console.error('Error getting controls:', error)
    }
  }

  const deleteHandler = async (Data) => {
    try {
      setIsDeleteModalOpen(true)
      setSelectedVendor(Data)
    } catch (error) {
      showErrorToast({message: 'Failed to delete vendor'})
    }
  }

  const handleConfirmDelete = async () => {
    try {
      setIsDeleting(true)
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const policyRef = collection(tenantRef, 'vendors')
      const docRef = doc(policyRef, selectedVendor.id)
      await deleteDoc(docRef)
      getVendor()

      showSuccessToast({
        message: `${selectedVendor.vendor_name} vendor has been deleted successfully!`,
      })

      const currentDate = new Date()
      const year = currentDate.getFullYear()
      const month = currentDate.getMonth() + 1
      const day = currentDate.getDate()
      const todaysDate = month + '/' + day + '/' + year

      const activitiesRef = collection(tenantRef, 'activities')
      await addDoc(activitiesRef, {
        message: `${currentUser?.userName} has deleted vendor '${selectedVendor.vendor_name}'`,
        timestamp: serverTimestamp(),
        tenantId: currentUser?.tenantId,
        uid: currentUser?.uid,
        action: 'Vendor deleted',
        target: 'Vendor Risk',
        username: currentUser?.userName,
        date: todaysDate,
      })
    } catch (error) {
      showErrorToast({message: 'Failed to delete vendor'})
    } finally {
      setIsDeleting(false)
      setIsDeleteModalOpen(false)
    }
  }

  const handleApplyFilter = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const category = e.target.value
    if (category === 'All') {
      handleResetFilter()
    } else {
      const filtered = vendor.filter((v) => v.risk_level.includes(category))
      setFilteredItems(filtered)
    }
  }

  useEffect(() => {
    if (searchTerm === '') {
      setFilteredItems(vendor)
      setSearchLength(false)
    } else {
      const filteredItems = vendor.filter((user) =>
        user.vendor_name.toLowerCase().includes(searchTerm)
      )
      setFilteredItems(filteredItems)
      setSearchLength(filteredItems.length === 0)
    }
  }, [vendor, searchTerm])

  const toggleDropdown = () => {
    setDropdownVisible(!isDropdownVisible)
  }

  const handleResetFilter = () => {
    setFilteredItems(vendor)
  }

  const handleOpenModal = (id) => {
    setIsModalOpen(true)
    setVendorID(id)
  }
  const handleCloseModal = () => {
    setIsModalOpen(false)
  }

  const handleArchive = async (item) => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const vendorsRef = collection(tenantRef, 'vendors')
      const docRef = doc(vendorsRef, item.id)

      await updateDoc(docRef, {
        archived: true,
      })

      const currentDate = new Date()
      const year = currentDate.getFullYear()
      const month = currentDate.getMonth() + 1
      const day = currentDate.getDate()

      const todaysDate = month + '/' + day + '/' + year

      const activitiesRef = collection(tenantRef, 'activities')
      await addDoc(activitiesRef, {
        message: `${currentUser?.userName} has archived vendor '${item.vendor_name}'`,
        timestamp: serverTimestamp(),
        tenantId: currentUser?.tenantId,
        uid: currentUser?.uid,
        action: 'Vendor archieved',
        target: 'Vendor Risk',
        username: currentUser?.userName,
        date: todaysDate,
      })

      setVendor((prevVendors) =>
        prevVendors.map((v) => (v.id === item.id ? {...v, archived: true} : v))
      )
      showSuccessToast({message: `${item.vendor_name} vendor has been archived successfully`})
    } catch (error) {
      showErrorToast({message: 'Failed to archive vendor'})
    }
  }

  const handleSelectAllChange = (e) => {
    const isChecked = e.target.checked
    setSelectAll(isChecked)
    setCheckedItems(isChecked ? filteredItems.map((item) => item.id) : [])
  }

  const handleCheckChange = (e) => {
    const {checked, value} = e.target
    setCheckedItems((prev) => {
      const newCheckedItems = checked ? [...prev, value] : prev.filter((item) => item !== value)

      setSelectAll(newCheckedItems.length === filteredItems.length)

      return newCheckedItems
    })
  }

  const handleDeselectClick = () => {
    setCheckedItems([])
    setSelectAll(false)
  }

  const handleMultipleDelete = async () => {
    setIsMultiDeleteModalOpen(true)
  }
  const handleConfirmMultiDelete = async () => {
    try {
      setIsMultiDeleting(true)
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const policyRef = collection(tenantRef, 'vendors')

      const deletePromises = checkedItems.map(async (vendorId) => {
        const docRef = doc(policyRef, vendorId)
        await deleteDoc(docRef)
      })

      await Promise.all(deletePromises)
      getVendor()
      showSuccessToast({message: 'Selected vendors have been deleted successfully!'})

      setCheckedItems([])
      setSelectAll(false)
    } catch (error) {
      showErrorToast({message: 'Failed to delete vendors'})
    } finally {
      setIsMultiDeleting(false)
      setIsMultiDeleteModalOpen(false)
    }
  }

  return (
    <>
      <Helmet>
        <title>SecureSlate | Vendor Risk</title>
      </Helmet>

      <div className='card-header border-0 pt-6 h-100'>
        <div className='card-title'>
          <div className='flex-lg-fill'>
            <Searchbar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
          </div>
        </div>
        <div className='card-toolbar'>
          <div className='d-flex justify-content-end' data-kt-user-table-toolbar='base'>
            {checkedItems.length > 0 ? (
              <>
                {checkedItems.length > 0 ? (
                  <div className='  border-end pe-4 text-center' style={{color: '#A1A5B7'}}>
                    <span>
                      <p className='pt-4'>{checkedItems.length} Selected</p>
                    </span>
                  </div>
                ) : null}
                <div style={{display: 'flex', alignItems: 'center'}}>
                  <div className='mx-5' style={{cursor: 'pointer'}} onClick={handleDeselectClick}>
                    <img src='/media/buttons/Deselect.svg' alt='Deselect' />
                  </div>

                  <Button
                    btnType='destructive'
                    className='me-2'
                    onClick={handleMultipleDelete}
                    disabled={currentUser?.role === 'Auditor' || !!currentAuditContext}
                  >
                    Delete vendors
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className='d-flex align-items-center justify-content-end '>
                  <select
                    className='form-select select2-hidden-accessible me-4'
                    data-control='select2'
                    data-hide-search='true'
                    data-placeholder='Status'
                    data-kt-ecommerce-product-filter='status'
                    data-select2-id='select2-data-9-zism'
                    tabIndex={-1}
                    aria-hidden='true'
                    data-kt-initialized='1'
                    style={{height: '40px'}}
                    value={selectedCategory}
                    onChange={(e) => {
                      setSelectedCategory(e.target.value)
                      if (e.target.value === 'All') {
                        handleResetFilter()
                      } else {
                        handleApplyFilter(e)
                      }
                    }}
                  >
                    <option onClick={handleResetFilter} value='All' selected>
                    Risk Level
                    </option>
                    <option value='Low'>Low</option>
                    <option value='Medium'>Medium</option>
                    <option value='High'>High</option>
                  </select>
                </div>
                <VendorListToolbar currentAuditContext={currentAuditContext} />
              </>
            )}
          </div>
        </div>
      </div>
      <div className='table-responsive py-3 px-10' style={{minHeight: '50vh'}}>
        <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
          <thead>
            <tr className='fw-bold fs-7' style={{color: '#5A5A5A'}}>
              <th className='w-25px'>
                <div className='form-check form-check-sm form-check-custom form-check-solid'>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    checked={selectAll}
                    data-kt-check='true'
                    data-kt-check-target='.widget-9-check'
                    onChange={handleSelectAllChange}
                  />
                </div>
              </th>
              <th className='min-w-300px'>VENDOR NAME</th>
              <th className='min-w-100px'>RISK LEVEL</th>
              <th className='min-w-150px'>DUE DILIGENCE</th>
              <th className='min-w-100px'>CATEGORY</th>
              <th className='min-w-150px'>OWNER</th>
              <th className='min-w-50px'></th>
            </tr>
          </thead>
          <UpdateVendor id={vendorID} isOpen={isModalOpen} onClose={handleCloseModal} />
          <tbody>
            {filteredItems && filteredItems.length > 0 ? (
              filteredItems.map((item) => (
                <tr>
                  <td>
                    <div className='form-check form-check-sm form-check-custom form-check-solid'>
                      <input
                        className='form-check-input widget-9-check'
                        type='checkbox'
                        value={item.id}
                        checked={checkedItems.includes(item.id)}
                        onChange={handleCheckChange}
                      />
                    </div>
                  </td>
                  <td>
                    <div className='d-flex align-items-center'>
                      <div className='symbol symbol-30px me-3'>
                        {item.logo && item.logo.length > 0 && item.logo[0]?.url ? (
                          <img src={toAbsoluteUrl(item.logo[0]?.url)} alt='' loading='lazy' />
                        ) : item.logoUrl ? (
                          <img src={toAbsoluteUrl(item.logoUrl)} alt='' loading='lazy' />
                        ) : (
                          <div className='symbol-label fw-bold fs-6' style={{ backgroundColor: '#F5F5F5', border: '1px solid #E5E5E5' }}>
                            {item.vendor_name ? item.vendor_name.split(' ').map(word => word.charAt(0)).join('').toUpperCase().slice(0, 2) : '--'}
                          </div>
                        )}
                      </div>
                      <div className='d-flex justify-content-start flex-column'>
                        <Link
                          to={`/vendor-risk/details/${item.id}`}
                          className='text-dark fw-bold text-hover-primary fs-6'
                        >
                          {item.vendor_name}
                        </Link>
                      </div>
                    </div>
                  </td>
                  <td>
                    {item.risk_level ? (
                      <a
                        href='#'
                        className={`badge fs-7 ${
                          item.risk_level === 'High'
                            ? 'badge-danger'
                            : item.risk_level === 'Medium'
                            ? 'badge-warning'
                            : 'badge-primary'
                        }`}
                        style={{
                          color:
                            item.risk_level === 'High'
                              ? '#EE3043'
                              : item.risk_level === 'Medium'
                              ? '#FFA800'
                              : '#0E9F6E',
                          background:
                            item.risk_level === 'High'
                              ? '#FFF5F8'
                              : item.risk_level === 'Medium'
                              ? '#FFF8DD'
                              : '#E8F8F4',
                        }}
                      >
                        {item.risk_level}
                      </a>
                    ) : (
                      '--'
                    )}
                  </td>

                  <td>
                    <div className='d-flex flex-column w-100 me-2'>
                      <div className='d-flex flex-stack mb-2'>
                        <div className='clearfix bshadow0 pbs fw-semibold'>
                          <span
                            className={`badge fs-7 ${
                              !item.due_diligence_status ||
                              item.due_diligence_status === 'Incomplete'
                                ? 'badge-danger'
                                : 'badge-primary'
                            }`}
                            style={{
                              color:
                                !item.due_diligence_status ||
                                item.due_diligence_status === 'Incomplete'
                                  ? '#EE3043'
                                  : '#0E9F6E',
                              background:
                                !item.due_diligence_status ||
                                item.due_diligence_status === 'Incomplete'
                                  ? '#FFF5F8'
                                  : '#E8F8F4',
                            }}
                          >
                            {item.due_diligence_status ? item.due_diligence_status : 'Incomplete'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className='d-flex align-items-center'>
                      <div className='d-flex justify-content-start flex-column'>
                        <a href='#' className='text-dark fw-semibold text-hover-primary fs-6'>
                          {item.category ? item.category : '--'}
                        </a>
                      </div>
                    </div>
                  </td>

                  <td>
                    <div className='d-flex align-items-center'>
                      {item.owner ? <OwnerDisplay owner={item.owner} uid={item.owner_uid} /> : <span>--</span>}
                    </div>
                  </td>
                  <td className=''>
                    <div className='dropdown mx-2'>
                      <button
                        className='btn btn-secondary border-0 p-0 m-2'
                        type='button'
                        id='dropdownMenu2'
                        data-bs-toggle='dropdown'
                        aria-haspopup='true'
                        aria-expanded='false'
                      >
                        <img
                          src='/media/buttons/Options.svg'
                          alt='Options'
                          onClick={toggleDropdown}
                        />
                      </button>
                      <ul className='dropdown-menu' aria-labelledby='dropdownMenu2'>
                        <li>
                          <Link to={`/vendor-risk/details/${item.id}`}>
                            <button
                              className='dropdown-item text-hover-primary'
                              type='button'
                              style={{color: '#262626'}}
                            >
                              <svg
                                xmlns='http://www.w3.org/2000/svg'
                                width='18'
                                height='18'
                                viewBox='0 0 24 24'
                                fill='none'
                                stroke='currentColor'
                                stroke-width='2'
                                stroke-linecap='round'
                                stroke-linejoin='round'
                                className='icon icon-tabler icons-tabler-outline icon-tabler-eye mx-1 mb-1'
                              >
                                <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                <path d='M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0' />
                                <path d='M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6' />
                              </svg>{' '}
                              View Vendor Details
                            </button>
                          </Link>
                        </li>
                        <li>
                          <button
                            className={`dropdown-item ${
                              currentUser?.role === 'Auditor' || !!currentAuditContext ? 'text-gray-600' : 'text-hover-primary'
                            } `}
                            type='button'
                            style={{color: '#262626'}}
                            onClick={() => handleOpenModal(item.id)}
                            disabled={currentUser?.role === 'Auditor' || !!currentAuditContext}
                          >
                            <svg
                              xmlns='http://www.w3.org/2000/svg'
                              width='18'
                              height='18'
                              viewBox='0 0 24 24'
                              fill='none'
                              stroke='currentColor'
                              stroke-width='2'
                              stroke-linecap='round'
                              stroke-linejoin='round'
                              className='icon icon-tabler icons-tabler-outline icon-tabler-edit mx-1 mb-1'
                            >
                              <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                              <path d='M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1' />
                              <path d='M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415z' />
                              <path d='M16 5l3 3' />
                            </svg>{' '}
                            Edit Vendor
                          </button>
                        </li>

                        <li>
                          <button
                            className={`dropdown-item ${
                              currentUser?.role === 'Auditor' || !!currentAuditContext ? 'text-gray-600' : 'text-hover-primary'
                            } `}
                            type='button'
                            style={{color: '#262626'}}
                            onClick={() => handleArchive(item)}
                            disabled={currentUser?.role === 'Auditor' || !!currentAuditContext}
                          >
                            <svg
                              xmlns='http://www.w3.org/2000/svg'
                              width='18'
                              height='18'
                              viewBox='0 0 24 24'
                              fill='none'
                              stroke='currentColor'
                              stroke-width='2'
                              stroke-linecap='round'
                              stroke-linejoin='round'
                              className='icon icon-tabler icons-tabler-outline icon-tabler-archive  mx-1 mb-1'
                            >
                              <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                              <path d='M3 4m0 2a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v0a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2z' />
                              <path d='M5 8v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-10' />
                              <path d='M10 12l4 0' />
                            </svg>{' '}
                            Archive Vendor
                          </button>
                        </li>

                        <li>
                          <button
                            className={`dropdown-item ${
                              currentUser?.role === 'Auditor' || !!currentAuditContext ? 'text-gray-600' : ''
                            } `}
                            type='button'
                            style={{color: '#F15363'}}
                            onClick={(e) => deleteHandler(item)}
                            disabled={currentUser?.role === 'Auditor' || !!currentAuditContext}
                          >
                            <svg
                              xmlns='http://www.w3.org/2000/svg'
                              width='18'
                              height='18'
                              viewBox='0 0 24 24'
                              fill='none'
                              stroke='currentColor'
                              strokeWidth='2'
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              className='icon icon-tabler icons-tabler-outline icon-tabler-trash mx-1 mb-1'
                            >
                              <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                              <path d='M4 7l16 0' />
                              <path d='M10 11l0 6' />
                              <path d='M14 11l0 6' />
                              <path d='M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12' />
                              <path d='M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3' />
                            </svg>{' '}
                            Delete Vendor
                          </button>
                        </li>
                      </ul>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <>
                <tr style={{height: '100%'}}>
                  {!searchLength ? (
                    <td colSpan={10}>
                      <div
                        className='d-flex justify-content-center align-items-center text-center'
                        style={{height: '100%'}}
                      >
                        <div className='m-20'>
                          <img src={toAbsoluteUrl('/media/buttons/emptystate.svg')} alt='' />
                          <div className='m-5 fs-5'>
                            <p style={{fontWeight: 'bold'}}>No vendor found!</p>
                          </div>
                          <p>
                            We couldn't find any vendor that match your search. Maybe try a
                            different search!
                          </p>
                        </div>
                      </div>
                    </td>
                  ) : (
                    <td colSpan={6}>
                      <div
                        className='d-flex justify-content-center align-items-center text-center'
                        style={{height: '100%'}}
                      >
                        {!showEmptyState ? (
                          <div className='m-20'>
                            <PolicyListLoading />
                          </div>
                        ) : (
                          <div className='m-20'>
                            <img src={toAbsoluteUrl('/media/buttons/emptystate.svg')} alt='' />
                            <div className='m-4 fs-5'>
                              <p style={{fontWeight: 'bold'}}>No vendors risk level matched</p>
                            </div>
                            <p>
                              We couldn't find any vendor that match your filter/search. Maybe try a
                              different search!
                            </p>
                          </div>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>

      <ConfirmationModal
        isModalOpen={isDeleteModalOpen}
        hideModal={() => setIsDeleteModalOpen(false)}
        confirmBtnClickHandler={handleConfirmDelete}
        toDeleteItemTitle={selectedVendor?.vendor_name}
        isLoading={isDeleting}
        title='Delete Vendor'
        message={`Are you sure you want to delete ${selectedVendor?.vendor_name}? This action cannot be undone.`}
      />

      <ConfirmationModal
        isModalOpen={isMultiDeleteModalOpen}
        hideModal={() => setIsMultiDeleteModalOpen(false)}
        confirmBtnClickHandler={handleConfirmMultiDelete}
        toDeleteItemTitle={`${checkedItems.length} vendors`}
        isLoading={isMultiDeleting}
        title='Delete Multiple Vendors'
        message={`Are you sure you want to delete ${checkedItems.length} selected vendors? This action cannot be undone.`}
      />
    </>
  )
}

export default VendorRisk
